import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { FaRegCalendar } from "react-icons/fa";
import tw from "tailwind-styled-components";
import { ActionSection, CombineInputGroup, FieldWrappers, InputGroup, Label, RupeeIcon, ScrollableContent, SideCancelBtn, SubmitBtn, Wrapper } from "../../Styles/InputStyles";
import CategoryCard from "../../CategoryCard";
import DropZone from "../../Notice/Dropzone";
import SelectItemModal from "../../SelectItemModal";
import SelectMultipleRoomModal from "../SelectMutipleRoomModal";
import SelectSingleRoomModal from "../SelectSingleRoomModal";

const UtilityBillForm = ({
    type,
    floorsData,
    floorsLoading,
    roomList,
    selectedFloor,
    selectedRoom,
    onFloorSelect,
    onRoomSelect,
    onBack,
    onSubmit,
    addBillLoading,
}) => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [endCalendarOpen, setEndCalendarOpen] = useState(false);
    const [floorModalOpen, setFloorModalOpen] = useState(false);
    const [roomModalOpen, setRoomModalOpen] = useState(false);

    const validationSchema = Yup.object().shape({
        floor: Yup.string().required(`Please select ${type === 1 ? "Floor" : "Flat"}`),
        roomId: Yup.string().required("Please select Room"),
        startDate: Yup.date().required("Start Date required").typeError("Start Date required"),
        endDate: Yup.date()
            .required("End Date required")
            .typeError("End Date required")
            .min(Yup.ref('startDate'), "End date must be after start date"),
        amount: Yup.number()
            .required("Amount required")
            .min(1, "Amount must be greater than 0")
            .max(50000, "Amount cannot exceed 50000")
            .typeError("Amount must be a number"),
        description: Yup.string().max(500, "Description too long"),
        file: Yup.mixed().nullable()
    });

    const getInitialValues = () => {
        return {
            floor: "",
            roomId: "",
            startDate: "",
            endDate: "",
            amount: "",
            description: "",
            file: null,
        };
    };

    const renderSelectedRoom = (selected = []) => {
        if (!selected || !selected.length) return `Select ${type === 1 ? "Room" : "Rooms"}`;

        const maxToShow = 2;
        const namesToShow = selected.slice(0, maxToShow).map(room => room.roomNum);
        const remainingCount = selected.length - maxToShow;

        return (
            <div className="flex text-left">
                <span>{namesToShow.join(", ")}</span>
                {remainingCount > 0 && (
                    <span className="ml-1">+{remainingCount} more</span>
                )}
            </div>
        );
    };

    return (
        <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ values, setFieldValue, resetForm, errors, touched }) => (
                <Form>
                    <ScrollableContent>
                        <Wrapper>
                            <InputGroup>
                                <Label>{type === 1 ? "Select Floor *" : "Select Flat *"}</Label>
                                <CategoryCard
                                    title={type === 1 ? "Floor" : "Flat"}
                                    selectedCategory={selectedFloor}
                                    onClick={() => setFloorModalOpen(true)}
                                    disabled={floorsLoading}
                                />
                                {errors.floor && touched.floor && (
                                    <div className="text-red-500 text-sm mt-1">{errors.floor}</div>
                                )}
                            </InputGroup>

                            <InputGroup>
                                <Label>Select Room *</Label>
                                {roomList.length > 0 ? (
                                    <>
                                        <CategoryCard
                                            title="Room"
                                            selectedCategory={
                                                selectedRoom && selectedRoom.length > 0
                                                    ? {
                                                        label: renderSelectedRoom(selectedRoom),
                                                        items: selectedRoom
                                                    }
                                                    : null
                                            }
                                            onClick={() => setRoomModalOpen(true)}
                                        />
                                        {errors.roomId && touched.roomId && (
                                            <div className="text-red-500 text-sm mt-1">{errors.roomId}</div>
                                        )}
                                    </>
                                ) : (
                                    <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                        <p className="text-gray-500 text-sm">
                                            {selectedFloor
                                                ? `There are no rooms in the selected ${type === 2 ? "flat" : "floor"}`
                                                : floorsLoading
                                                    ? "Loading data..."
                                                    : `Please select a ${type === 2 ? "flat" : "floor"} first`
                                            }
                                        </p>
                                    </div>
                                )}
                            </InputGroup>

                            <CombineInputGroup>
                                <InputGroup>
                                    <Label htmlFor="startDate">Start Date *</Label>
                                    <FieldWrappers className={
                                        touched.startDate && errors.startDate
                                            ? "border-rose-400 relative w-full"
                                            : "border-gray-300 relative w-full"
                                    }>
                                        <DatePicker
                                            //selected={values.startDate ? new Date(values.startDate) : null}
                                            selected={values.startDate ? moment(values.startDate, "YYYY-MM-DD").toDate() : null}
                                            onChange={(date) =>
                                                setFieldValue("startDate", moment(date).format("YYYY-MM-DD"))
                                            }
                                            dateFormat="yyyy-MM-dd"
                                            className="cursor-pointer"
                                            placeholderText="YYYY-MM-DD"
                                            onClickOutside={() => setCalendarOpen(false)}
                                            onSelect={() => setCalendarOpen(false)}
                                            open={calendarOpen}
                                            onInputClick={() => setCalendarOpen(true)}
                                            popperPlacement="top-end"
                                            showIcon
                                            icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                            showPopperArrow={false}
                                            onFocus={(e) => e.target.blur()}
                                            wrapperClassName="w-full"
                                            portalId="root-portal"
                                        />
                                    </FieldWrappers>
                                </InputGroup>

                                <InputGroup>
                                    <Label htmlFor="endDate">End Date *</Label>
                                    <FieldWrappers className={
                                        touched.endDate && errors.endDate
                                            ? "border-rose-400 relative w-full"
                                            : "border-gray-300 relative w-full"
                                    }>
                                        <DatePicker
                                            //selected={values.endDate ? new Date(values.endDate) : null}
                                            selected={values.endDate ? moment(values.endDate, "YYYY-MM-DD").toDate() : null}
                                            onChange={(date) =>
                                                setFieldValue("endDate", moment(date).format("YYYY-MM-DD"))
                                            }
                                            minDate={values.startDate ? new Date(values.startDate) : null}
                                            dateFormat="yyyy-MM-dd"
                                            className="cursor-pointer"
                                            placeholderText="YYYY-MM-DD"
                                            onClickOutside={() => setEndCalendarOpen(false)}
                                            onSelect={() => setEndCalendarOpen(false)}
                                            open={endCalendarOpen}
                                            onInputClick={() => setEndCalendarOpen(true)}
                                            popperPlacement="top-end"
                                            showIcon
                                            icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                            showPopperArrow={false}
                                            onFocus={(e) => e.target.blur()}
                                            wrapperClassName="w-full"
                                            portalId="root-portal"
                                        />
                                    </FieldWrappers>
                                </InputGroup>
                            </CombineInputGroup>

                            <InputGroup>
                                <Label>Amount *</Label>
                                <FieldWrappers>
                                    <RupeeIcon />
                                    <Field
                                        name="amount"
                                        type="number"
                                        placeholder="Enter Amount"
                                        className="p-2 rounded w-full pl-5 pr-4"
                                    />
                                </FieldWrappers>
                                {errors.amount && touched.amount && (
                                    <div className="text-red-500 text-sm mt-1">{errors.amount}</div>
                                )}
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="description">Description (optional)</Label>
                                <FieldWrapper>
                                    <Field
                                        as="textarea"
                                        id="description"
                                        name="description"
                                        rows={4}
                                        maxLength={500}
                                        placeholder="Write here..."
                                        className="min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y break-words overflow-hidden"
                                    />
                                </FieldWrapper>
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="file">Document (optional)</Label>
                                <FieldWrapper $select={true}>
                                    <Field
                                        name="file"
                                        type="text"
                                        id="file"
                                        autoComplete="off"
                                    >
                                        {(props) => (
                                            <DropZone
                                                fields={props.field}
                                                setFieldValue={props.form.setFieldValue}
                                                componentFor="expense"
                                                acceptedFiles="image/*,application/pdf"
                                                File={props.field.value ? [props.field.value] : null}
                                            />
                                        )}
                                    </Field>
                                </FieldWrapper>
                            </InputGroup>
                        </Wrapper>
                    </ScrollableContent>

                    <ActionSection>
                        <SideCancelBtn type="button" onClick={() => {
                            resetForm();
                            onBack();
                        }}>
                            Back
                        </SideCancelBtn>
                        <SubmitBtn type="submit" disabled={addBillLoading || floorsLoading}>
                            {addBillLoading ? "Submitting..." : "Submit"}
                        </SubmitBtn>
                    </ActionSection>

                    <SelectItemModal
                        isOpen={floorModalOpen}
                        onClose={() => setFloorModalOpen(false)}
                        onSelect={(floor) => {
                            onFloorSelect(floor, setFieldValue);
                            setFloorModalOpen(false);
                        }}
                        options={floorsData}
                        selectedItem={selectedFloor}
                        title={type === 2 ? "Select Flat" : "Select Floor"}
                        searchPlaceholder={type === 2 ? "Search flats..." : "Search floors..."}
                        idKey="id"
                        nameKey="name"
                        descriptionKeys={["propertyName"]}
                        emptyMessage="No floors available"
                        noResultsMessage="No floors found"
                        showClearSelection={true}
                        isLoading={floorsLoading}
                    />

                    {type === 2 && (
                        <SelectMultipleRoomModal
                            isOpen={roomModalOpen}
                            onClose={() => setRoomModalOpen(false)}
                            onSelect={(room) => {
                                onRoomSelect(room, setFieldValue);
                                setRoomModalOpen(false);
                            }}
                            options={roomList}
                            selectedItem={selectedRoom}
                            title="Select Room"
                            searchPlaceholder="Search rooms..."
                            idKey="id"
                            nameKey="roomNum"
                            descriptionKeys={["amenities"]}
                            emptyMessage="No rooms available"
                            noResultsMessage="No rooms found"
                            showClearSelection={true}
                        />
                    )}

                    {type === 1 && (
                        <SelectSingleRoomModal
                            isOpen={roomModalOpen}
                            onClose={() => setRoomModalOpen(false)}
                            onSelect={(room) => {
                                onRoomSelect(room, setFieldValue);
                                setRoomModalOpen(false);
                            }}
                            options={roomList}
                            selectedItem={selectedRoom}
                            title="Select Room"
                            searchPlaceholder="Search rooms..."
                            idKey="id"
                            nameKey="roomNum"
                            descriptionKeys={["amenities"]}
                            emptyMessage="No rooms available"
                            noResultsMessage="No rooms found"
                            showClearSelection={true}
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

// Styled Components
const FieldWrapper = tw.div`w-full`;

export default UtilityBillForm;
